
import React, { useState, useCallback } from 'react';
import { Table, DatePicker, Button, Select, message, Alert, Spin } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import moment from 'moment';
import debounce from 'lodash/debounce';
import { cashFlow, getCustomerQb } from '~/services/hooks/quickbooksApi';
import './style.css';
import { useTranslation } from 'react-i18next';
import { parseCashFlowData } from '~/Utils/parseReportData';

const { RangePicker } = DatePicker;

export default function CashFlowReport() {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState([moment().startOf('year'), moment().endOf('year')]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportLoaded, setReportLoaded] = useState(false);
  const [noData, setNoData] = useState(false);
  const [triggeredManually, setTriggeredManually] = useState(false);

  const resetReport = () => {
    setSelectedCustomer(null);
    setCustomerOptions([]);
    setDataSource([]);
    setReportLoaded(false);
    setNoData(false);
    setTriggeredManually(false);
  };

  const hasRows = arr => Array.isArray(arr) && arr.length > 0;

  const handleCustomerSearch = async value => {
    if (!value) {
      setCustomerOptions([]);
      return;
    }

    try {
      const customerData = await getCustomerQb({ name: encodeURIComponent(value) });
      const customers = customerData?.QueryResponse?.Customer ?? [];

      const options = customers.map(c => ({
        label: c.FullyQualifiedName,
        value: c.Id,
      }));

      setCustomerOptions(options);
    } catch (err) {
      console.error(err);
      message.error(t('screens:reports.customerSearchError'));
    }
  };

  const debouncedSearch = useCallback(debounce(handleCustomerSearch, 500), []);

  const fetchData = async (startDate, endDate) => {
    setLoading(true);
    setReportLoaded(false);
    setNoData(false);

    const reportData = await cashFlow({
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      customerId: selectedCustomer?.id,
    });

    if (hasRows(reportData?.Rows?.Row)) {
      setDataSource(parseCashFlowData(reportData));
      setReportLoaded(true);
    } else {
      setDataSource([]);
      setNoData(true);
    }

    setLoading(false);
  };

  const columns = [
    {
      title: 'Account',
      dataIndex: 'account',
      key: 'account',
      render: (text, record) => <span style={{ paddingLeft: record.level * 20 }}>{text}</span>,
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      render: text =>
        text === '' ? '' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(text)),
    },
  ];

  return (
    <Spin spinning={loading} tip={t('screens:reports.loading')} size="large" style={{ width: '100%' }}>
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <RangePicker value={dateRange} onChange={dates => setDateRange(dates)} format="YYYY-MM-DD" />

        <div style={{ display: 'flex', gap: 0, flexWrap: 'wrap' }}>
          <Select
            showSearch
            placeholder={t('screens:reports.customerSearchPlaceholder')}
            style={{ width: 600 }}
            filterOption={false}
            onSearch={debouncedSearch}
            onChange={(value, option) => {
              setSelectedCustomer({ id: value, name: option.label });
              setTriggeredManually(false);
            }}
            notFoundContent={null}
            options={customerOptions}
            value={selectedCustomer?.id || undefined}
          />
          {selectedCustomer && (
            <Button icon={<CloseOutlined />} onClick={resetReport} />
          )}
        </div>

        <Button
          type="primary"
          onClick={() => {
            setTriggeredManually(true);
            fetchData(dateRange[0], dateRange[1]);
          }}
          loading={loading}
        >
          {t('screens:reports.generateReport')}
        </Button>
      </div>

      {reportLoaded && triggeredManually && selectedCustomer?.name && (
        <h2 style={{ padding: '20px 0' }}>{selectedCustomer.name}</h2>
      )}

      {noData && (
        <Alert
          message={t('screens:reports.noData')}
          description={t('screens:reports.noDataDescription')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        bordered
        rowClassName={record => (record.isSummary ? 'summary-row' : '')}
      />
    </Spin>
  );
}
